import React, { useState, useEffect } from "react";
import axios from "axios";
import { useParams } from "react-router-dom";
import { SanitizeHTML } from "@bindeleddet/core/components";
import { Company, Job, Event, FavoriteJob } from "@bindeleddet/core/types";
import { Row, Typography, Tabs, Card, Empty } from "antd";
import type { TabsProps } from "antd";
import { useViewport } from "@bindeleddet/core/hooks";
import { useLoginContext, isCompanyUser } from "@bindeleddet/core/contexts/login";
import SingleEventBox from "containers/Events/SingelEventBox/SingleEventBox";
import SingleJobPostingBox from "containers/JobPostings/SingleJobPostingBox/SingleJobPostingBox";
import FollowCompanyButton from "components/FollowCompanyButton/FollowCompanyButton";
import { StyledHr } from "components/AboutCompany/style";
import { formatTimeNorwegian } from "helpers/dateUtils";
import CompanyHeader from "./CompanyHeader/CompanyHeader";

const { Title } = Typography;

const SingleCompany = () => {
  const [company, setCompany] = useState<Company>();
  const [jobs, setJobs] = useState<Job[]>([]);
  const [events, setEvents] = useState<Event[]>([]);
  const [favoriteJobs, setFavoriteJobs] = useState<FavoriteJob[]>([]);
  const [favoritesLoading, setFavoritesLoading] = useState<boolean>(false);

  const { width } = useViewport();
  const breakpoint = 950;

  const { id } = useParams<{ id: string }>();
  const { currentUser } = useLoginContext();

  useEffect(() => {
    axios.get<Company>(`/companies/${id}/`).then(response => setCompany(response.data));
  }, [id]);

  useEffect(() => {
    axios.get<Job[]>(`/jobs/`).then(response => setJobs(response.data.filter(job => job.company.toString() === id)));
  }, [id]);

  useEffect(() => {
    axios
      .get<Event[]>(`/events/`)
      .then(response => setEvents(response.data.filter(event => event.company.toString() === id)));
  }, [id]);

  useEffect(() => {
    if (!currentUser || isCompanyUser(currentUser)) return;
    setFavoritesLoading(true);
    axios.get<FavoriteJob[]>("/jobs/favorites/").then(response => {
      setFavoriteJobs(response.data);
      setFavoritesLoading(false);
    });
  }, [currentUser]);

  const getTime = (time: string) => {
    return formatTimeNorwegian(time);
  };

  const items: TabsProps["items"] = [
    {
      key: "1",
      label: `Stillingsannonser`,
      children:
        jobs.length === 0 ? (
          <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="Ingen aktuelle stillingsannonser" />
        ) : (
          jobs.map(job => {
            return (
              <div key={job.id} style={{ marginBottom: "5px" }}>
                <SingleJobPostingBox
                  job={job}
                  favoriteJobs={favoriteJobs}
                  favoritesLoading={favoritesLoading}
                  onFavoriteJobsChange={setFavoriteJobs}
                />
              </div>
            );
          })
        ),
    },
    {
      key: "3",
      label: `Arrangementer`,
      children:
        events.length === 0 ? (
          <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="Ingen kommende arrangementer" />
        ) : (
          events.map(event => {
            return (
              <div key={event.id} style={{ marginBottom: "5px" }}>
                <SingleEventBox
                  id={event.id}
                  title={event.name}
                  time={getTime(event.start)}
                  date={event.start}
                  location={event.location}
                  company={event.company}
                  registrationStatus={event.registration_status}
                />
              </div>
            );
          })
        ),
    },
  ];

  if (!company) return null;
  return (
    <>
      <CompanyHeader company={company} />
      <Row style={{ width: "75%", margin: "auto" }}>
        <Row>
          <Title level={2}>Om {company.name}</Title>
          <StyledHr />
        </Row>
        <Row justify="start" style={{ width: "100%" }}>
          <div style={{ minWidth: 200, maxWidth: 400, marginBottom: "10px" }}>
            <FollowCompanyButton companyId={company.id} companyName={company.name} size="small" />
          </div>
        </Row>
        <SanitizeHTML html={company.description} />
      </Row>
      <Card style={width < breakpoint ? { width: "95%", margin: "20px auto" } : { width: "75%", margin: "20px auto" }}>
        <Tabs centered items={items} />
      </Card>
    </>
  );
};

export default SingleCompany;
